'use client';

import React, { useEffect, useState } from 'react';
import NextImage from 'next/image';
import { css, cx } from 'styled-system/css';

import { Skeleton } from '@shaojiang/components/ui/skeleton';

import { BLUR_DATA_URL } from './blurDataUrl';

interface ShaojiangImageProps {
  className?: string;
  placeholderSrc?: string;
  /** Render as a div with a cover background image, so children can be overlaid */
  asBackground?: boolean;
  children?: React.ReactNode;
  src: string;
  alt: string;
  width?: number;
  height?: number;
  onClick?: () => void;
  style?: React.CSSProperties;
}

/** Image with a skeleton while loading and an optional low-res placeholder. */
export default function ShaojiangImage({
  className,
  src,
  placeholderSrc,
  asBackground,
  children = null,
  alt = '',
  width = 0,
  height = 0,
  onClick,
  style = {},
}: ShaojiangImageProps) {
  const [loaded, setLoaded] = useState(false);
  const [placeholderLoaded, setPlaceholderLoaded] = useState(false);

  useEffect(() => {
    if (src && (!placeholderSrc || placeholderLoaded)) {
      const image = new Image();
      image.onload = () => setLoaded(true);
      image.src = src;
    }
  }, [placeholderLoaded, placeholderSrc, src]);

  useEffect(() => {
    if (placeholderSrc) {
      const image = new Image();
      image.onload = () => setPlaceholderLoaded(true);
      image.src = placeholderSrc;
    }
  }, [placeholderSrc]);

  const displayedImageURL = placeholderSrc ? (placeholderLoaded ? src : placeholderSrc) : src;

  if (asBackground) {
    const classes = cx(
      css({
        position: 'relative',
        bgRepeat: 'no-repeat',
        bgPosition: 'center',
        bgSize: 'cover',
        boxShadow: 'lg',
      }),
      className,
    );
    const w = width || 'auto';
    return loaded ? (
      <div
        onClick={onClick}
        className={classes}
        style={{ ...style, height, width: w, backgroundImage: `url(${displayedImageURL})` }}
      >
        {children}
      </div>
    ) : (
      <div className={classes} style={{ height, width: w }}>
        <Skeleton height='full' borderRadius='inherit' />
        {children}
      </div>
    );
  }

  if (!loaded) {
    return (
      <Skeleton
        borderRadius='l2'
        maxW='full'
        maxH='400px'
        style={{ height: height || 160, width: width || '100%' }}
      />
    );
  }

  if (width) {
    return (
      <NextImage
        onClick={onClick}
        src={displayedImageURL}
        alt={alt}
        width={width}
        height={height}
        style={style}
        className={className}
        placeholder='blur'
        blurDataURL={BLUR_DATA_URL}
      />
    );
  }

  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={displayedImageURL}
      alt={alt}
      style={{ ...style, height: height || 'auto' }}
      className={cx(css({ display: 'inline-block' }), className)}
      onClick={onClick}
    />
  );
}
